import { useMemo } from 'react';
import { makeStyles, Tooltip } from '@material-ui/core';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  sparkline: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 0,
  },
  sparkWeek: {
    display: 'flex',
    flexDirection: 'column' as const,
    borderRight: `1px solid rgba(0,0,0,0.08)`,
    paddingRight: 2,
    marginRight: 2,
    '&:last-child': {
      borderRight: 'none',
      paddingRight: 0,
      marginRight: 0,
    },
  },
  sparkBars: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 1,
    height: 24,
  },
  sparkBarWrap: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    minWidth: 2,
    height: '100%',
    padding: '0 1px',
    '&:hover $sparkBar': {
      backgroundColor: '#A07BC8',
    },
  },
  sparkBar: {
    width: '100%',
    borderRadius: '2.5px 2.5px 1px 1px',
    backgroundColor: '#6B7FA3',
    transition: 'background-color 0.1s',
  },
  sparkWeekLabel: {
    fontSize: 7,
    color: theme.palette.gray.g2,
    opacity: 0.5,
    textAlign: 'left' as const,
    marginTop: 2,
    whiteSpace: 'nowrap' as const,
    lineHeight: 1,
  },
}));

interface WeekGroup {
  label: string;
  days: { date: string; hours: number }[];
}

/** Group daily data into ISO weeks */
export function groupByWeek(dailyData: { date: string; hours: number }[]): WeekGroup[] {
  const weekMap = new Map<string, WeekGroup>();
  for (const d of dailyData) {
    const weekStart = moment(d.date).startOf('isoWeek');
    const key = weekStart.format('YYYY-MM-DD');
    if (!weekMap.has(key)) {
      weekMap.set(key, { label: weekStart.locale('en').format('D MMM'), days: [] });
    }
    weekMap.get(key)!.days.push(d);
  }
  return Array.from(weekMap.values());
}

/** Build a 30-day weekday series (Mon-Fri only) ending today */
export function build30DaySeries(): { date: string; hours: number }[] {
  const result: { date: string; hours: number }[] = [];
  const cursor = moment().subtract(30, 'days');
  const today = moment();
  while (cursor.isSameOrBefore(today, 'day')) {
    if (cursor.isoWeekday() <= 5) {
      result.push({ date: cursor.format('YYYY-MM-DD'), hours: 0 });
    }
    cursor.add(1, 'day');
  }
  return result;
}

/**
 * Activity sparkline — shows daily hours as bars grouped by week.
 *
 * @param dailyHours - actual hours data per date
 * @param maxHours - max hours across all sparklines (for consistent scale)
 * @param showLabels - show week start date labels below
 * @param highlightDays - number of most recent days to show at full opacity (rest dimmed)
 */
const ActivitySparkline = ({
  dailyHours,
  maxHours,
  showLabels = false,
  highlightDays,
  height,
}: {
  dailyHours: { date: string; hours: number }[];
  maxHours: number;
  showLabels?: boolean;
  highlightDays?: number;
  height?: number;
}) => {
  const classes = useStyles();
  const hoursMap = useMemo(() => new Map(dailyHours.map(d => [d.date, d.hours])), [dailyHours]);

  // Always use 30-day series
  const series = useMemo(() => build30DaySeries(), []);
  const weeks = useMemo(() => groupByWeek(series), [series]);

  // Cutoff date for highlight range
  const cutoffDate = useMemo(() => {
    if (!highlightDays) return null;
    return moment().subtract(highlightDays, 'days').format('YYYY-MM-DD');
  }, [highlightDays]);

  return (
    <div className={classes.sparkline}>
      {weeks.map(week => (
        <div key={week.label} className={classes.sparkWeek} style={{ flex: week.days.length }}>
          <div className={classes.sparkBars} style={height ? { height } : undefined}>
            {week.days.map(d => {
              const hours = hoursMap.get(d.date) || 0;
              const pct = hours > 0 ? Math.max((hours / maxHours) * 100, 8) : 0;
              const dimmed = cutoffDate && d.date < cutoffDate;

              const bar = (
                <div key={d.date} className={classes.sparkBarWrap} style={{ opacity: dimmed ? 0.25 : 1 }}>
                  <div
                    className={classes.sparkBar}
                    style={{
                      height: hours > 0 ? `${pct}%` : 0,
                      minHeight: hours > 0 ? 4 : 0,
                    }}
                  />
                </div>
              );
              if (hours > 0) {
                return (
                  <Tooltip key={d.date} title={`${moment(d.date).locale('en').format('ddd, MMM D')}: ${hours.toFixed(1)}h`} arrow placement="top">
                    {bar}
                  </Tooltip>
                );
              }
              return bar;
            })}
          </div>
          {showLabels && week.days.length >= 3 && (
            <div className={classes.sparkWeekLabel}>{week.label}</div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ActivitySparkline;
